import React, { useEffect, useState } from 'react';
import Loader from '../../../components/Loader';
import Paginator from '../../../components/Paginator';
import ApiService from '../../../services/ApiService';
import { toast } from 'react-toastify';
import BookingDetailsSidebar from '../../../components/BookingDetailsSidebar';

const MAROON = '#c4941a';

const STATUS_COLORS = {
  pending:   { bg: '#FFF3CD', border: '#FFC107', text: '#7B4F00' },
  confirmed: { bg: '#DBEAFE', border: '#2563EB', text: '#1E40AF' },
  completed: { bg: '#D1FAE5', border: '#059669', text: '#065F46' },
  cancelled: { bg: '#FEE2E2', border: '#DC2626', text: '#7F1D1D' },
};

const StatusBadge = ({ status }) => {
  const s = status?.toLowerCase();
  const c = STATUS_COLORS[s] || { bg: '#F3F4F6', border: '#9CA3AF', text: '#374151' };
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      textTransform: 'capitalize',
    }}>
      {status || 'N/A'}
    </span>
  );
};

const CustomerBookingHistory = ({ userId }) => {
  const [isLoader, setIsLoader] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const getBookings = async () => {
    try {
      setIsLoader(true);
      const params = new URLSearchParams({ user_id: userId, page, limit: 10 });
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      const res = await ApiService.request({ method: 'GET', url: `admin/bookings?${params}` });
      if (res.data.status) {
        setBookings(res.data.data || []);
        setPagination(res.data.pagination || {});
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setIsLoader(false);
    }
  };

  useEffect(() => { getBookings(); }, [userId, page, search, status]); // eslint-disable-line

  return (
    <div className="card">
      <div className="card-header border-0 pt-6">
        <h3 className="card-title fw-bold m-0">Booking History</h3>
        <div className="card-toolbar d-flex gap-3">
          <div className="position-relative" style={{ width: 220 }}>
            <i className="bi bi-search position-absolute" style={{ left: 12, top: '50%', transform: 'translateY(-50%)', color: '#999' }}></i>
            <input
              type="text"
              className="form-control ps-10"
              placeholder="Search bookings…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <select className="form-select" style={{ width: 150 }} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Confirmed">Confirmed</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="card-body pt-4">
        {isLoader && bookings.length === 0 ? (
          <div className="text-center py-10"><Loader text="Loading bookings…" size={30} /></div>
        ) : bookings.length === 0 ? (
          <div className="text-center text-muted py-10">
            <i className="bi bi-calendar-x fs-2x mb-3 d-block"></i>
            <p className="mb-0">No bookings found</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle table-row-dashed fs-6 gy-4">
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr className="text-start text-muted fw-bold fs-7 text-uppercase">
                  <th>Booking</th>
                  <th>partner</th>
                  <th>Date & Time</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="fw-semibold text-gray-600">
                {bookings.map((bk) => (
                  <tr key={bk._id} style={{ cursor: 'pointer' }} onClick={() => { setSelectedBookingId(bk._id); setShowSidebar(true); }}>
                    <td>
                      <span className="text-gray-800 fw-bold">
                        {bk.booking_code || `#${bk._id?.toString().slice(-6)}`}
                      </span>
                    </td>
                    <td>{bk.partner_id?.company_name || 'N/A'}</td>
                    <td>
                      {bk.event_date ? new Date(bk.event_date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                      {bk.event_time && <span className="text-muted ms-1">{bk.event_time}</span>}
                    </td>
                    <td style={{ color: MAROON, fontWeight: 700 }}>AED {bk.grand_total ?? bk.total_amount ?? 0}</td>
                    <td><StatusBadge status={bk.status} /></td>
                    <td>
                      <button className="btn btn-icon btn-light-primary btn-sm" onClick={(e) => { e.stopPropagation(); setSelectedBookingId(bk._id); setShowSidebar(true); }}>
                        <i className="bi bi-eye-fill"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.total_pages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-5">
            <span className="text-muted fs-7">{bookings.length} of {pagination.total_records} bookings</span>
            <Paginator page={pagination.current_page} rows={pagination.per_page} totalRecords={pagination.total_records} onPageChange={setPage} />
          </div>
        )}
      </div>

      {showSidebar && (
        <>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{ visibility: 'visible', width: 571 }}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Booking Details</h5>
              <button type="button" className="btn-close" onClick={() => setShowSidebar(false)}></button>
            </div>
            <div className="offcanvas-body booking-offcanvas-body">
              <BookingDetailsSidebar bookingId={selectedBookingId} onUpdate={getBookings} />
            </div>
          </div>
          <div className="offcanvas-backdrop fade show" onClick={() => setShowSidebar(false)}></div>
        </>
      )}
    </div>
  );
};

export default CustomerBookingHistory;
